/**
 * Silent populate for the "lookup-was-null" cache state.
 *
 * Why this exists: a phone whose `useWhatsAppConversation` lookup
 * resolved to null (no Voxa conversation existed at lookup time) sits
 * in React Query as a cached `null`. The router's primary path matches
 * by `conversation.id === conversationId`, which can never hit a `null`
 * — so the very first webhook that creates that conversation on Voxa's
 * side would otherwise leave the page stuck on the "no conversation"
 * composer until the user refreshes.
 *
 * This helper closes that gap WITHOUT a loading state. Given the
 * incoming row's `conversation_id`, it:
 *
 *   1. Fetches the conversation from Voxa (1 API call) to learn its
 *      `phoneNumber` — the bridge between the row (which doesn't carry
 *      a phone field) and the cache keys (which are phone-scoped).
 *   2. Matches that phone against any null-cached lookup queries in the
 *      same company.
 *   3. For a match: `setQueryData` on the lookup with the fetched
 *      conversation, and pre-populate the first timeline page so the
 *      `useInfiniteQuery` mounting later reads cached data instead of
 *      firing a load.
 *
 * Net cost: 2 Voxa calls per webhook that lacks a non-null match AND has
 * at least one null-cached lookup in the company. No-op when nothing
 * matches.
 *
 * Covers Case 2 (user on the page now — page transitions to the active
 * conversation UI silently) AND Case 3b (user not on the page — cache is
 * fresh for next visit; still no loading state then either).
 */
import type {
  HatifConversation,
  HatifConversationTimelineEvent,
} from '@dallali/notifications';
import type {
  InfiniteData,
  QueryClient,
  QueryKey,
} from '@tanstack/react-query';

import { normalizePhoneForDB } from '@/lib/phone';
import {
  hatifGetConversation,
  hatifGetConversationTimeline,
} from '@/services/api';

// Must match useWhatsAppConversation.TIMELINE_PAGE_SIZE so the cache
// shape we seed mirrors what the page would have written itself.
const TIMELINE_PAGE_SIZE = 50;

interface TimelinePage {
  items: HatifConversationTimelineEvent[];
  skip: number;
  originalSize: number;
  total: number;
}

export interface ResolveNewConversationArgs {
  queryClient: QueryClient;
  companyId: string;
  conversationId: string;
  /** All cached lookups for this company — predicate-matched by the
   * caller. Each tuple is `[queryKey, value]`; we only act on the ones
   * whose value is `null`. */
  lookups: ReadonlyArray<[QueryKey, HatifConversation | null | undefined]>;
}

export async function resolveNewConversation({
  queryClient,
  companyId,
  conversationId,
  lookups,
}: ResolveNewConversationArgs): Promise<void> {
  const nullLookups = lookups.filter(([, conv]) => conv === null);
  if (nullLookups.length === 0) return;

  let conv: HatifConversation;
  try {
    conv = await hatifGetConversation(companyId, conversationId);
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'unknown error';
    console.warn(
      `resolveNewConversation(${conversationId}) — hatifGetConversation failed (${msg})`
    );
    return;
  }

  const convPhone = normalizePhoneForDB(conv.phoneNumber ?? '');
  if (!convPhone) return;

  // Find every null-cached lookup whose (channelId, phone) matches the
  // conversation we just fetched. Channel match because conversations
  // are channel-scoped — a lookup for a different channel is a
  // different cache subtree even if the phone is identical.
  for (const [lookupKey] of nullLookups) {
    const key = lookupKey as readonly unknown[];
    const channelId = key[2];
    const phone = key[3];
    if (typeof channelId !== 'string' || typeof phone !== 'string') continue;
    if (conv.channelId && conv.channelId !== channelId) continue;
    if (normalizePhoneForDB(phone) !== convPhone) continue;

    // Flip the lookup from null to the resolved conversation. The active
    // useQuery observer (Case 2) re-renders with the new data and the
    // gated timelineQuery becomes enabled.
    queryClient.setQueryData(lookupKey, conv);

    // Seed the timeline cache so the timelineQuery — which is about to
    // mount — finds data already present and doesn't fire its initial
    // load. (React Query will still do a background revalidate per its
    // default staleTime, but background refetches don't show a spinner.)
    const timelineQueryKey: QueryKey = [
      'hatif-conversation',
      companyId,
      channelId,
      phone,
      'timeline',
      conversationId,
    ];

    let initial;
    try {
      initial = await hatifGetConversationTimeline(companyId, conversationId, {
        SkipCount: 0,
        MaxResultCount: TIMELINE_PAGE_SIZE,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'unknown error';
      console.warn(
        `resolveNewConversation(${conversationId}) — timeline seed failed (${msg})`
      );
      // Lookup is still populated; let the page's own first fetch take
      // care of the timeline. It'll show a spinner this once — degraded
      // but correct. Use `continue` (not `return`) so other matching
      // null lookups in the iteration still get processed.
      continue;
    }

    const items = initial.items ?? [];
    queryClient.setQueryData<InfiniteData<TimelinePage>>(timelineQueryKey, {
      pages: [
        {
          items,
          skip: 0,
          originalSize: items.length,
          total: initial.totalCount,
        },
      ],
      pageParams: [0],
    });
  }
}
